import { useLocaleRedirection } from 'modules/auth/hooks/useLocaleRedirection'
import { useDisplayFatcaRefusedModalOnMount } from 'modules/auth/hooks/useDisplayFatcaRefusedModalOnMount'
import { useRequiredAuthDataQueries } from 'modules/auth/hooks/useRequiredAuthDataQueries'
import { useDisplayTokamakMigrationBlockingModalOnMount } from 'modules/bank/hooks/card/useDisplayTokamakMigrationBlockingModalOnMount'
import { ErrorReload } from 'modules/common/components/ErrorReload/ErrorReload'
import { ZendeskMessagingScript } from 'modules/common/components/Scripts/ZendeskMessagingScript'
import { useIsMounted } from 'modules/common/hooks/useIsMounted'
import { useTranslation } from 'modules/common/hooks/useTranslation'
import { EnumRoutes } from 'modules/common/routes'
import { useRouter } from 'next/router'
import { PropsWithChildren, useEffect } from 'react'
import { FullPageLoader } from '../FullPageLoader/FullPageLoader'
import { RedirectAuthenticatedUserWrapper } from '../RedirectAuthenticatedUserWrapper/RedirectAuthenticatedUserWrapper'

export const AuthenticatedPageGuard = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation('common-components')
  const isMounted = useIsMounted()
  const router = useRouter()

  useLocaleRedirection()

  const {
    data: { internalAccount, corporate },
    isFetchError,
    isFetchLoading,
    isUserAuthenticated,
    errorRefetch,
  } = useRequiredAuthDataQueries()

  useDisplayTokamakMigrationBlockingModalOnMount({ internalAccount })
  useDisplayFatcaRefusedModalOnMount({ corporate })

  useEffect(() => {
    if (!isUserAuthenticated) {
      router.replace({
        pathname: EnumRoutes.LOGIN,
        query: { redirectURL: router.asPath },
      })
    }
  }, [router, isUserAuthenticated])

  if (isFetchError) {
    return (
      <ErrorReload
        className="min-h-screen"
        title={t('authenticatedGuard.error.title')}
        firstDescription={t('authenticatedGuard.error.firstDescription')}
        secondDescription={t('authenticatedGuard.error.secondDescription')}
        onReload={errorRefetch}
      />
    )
  } else if (!isMounted || !isUserAuthenticated || isFetchLoading) {
    return <FullPageLoader />
  } else {
    return (
      <RedirectAuthenticatedUserWrapper>
        {children}
        <ZendeskMessagingScript />
      </RedirectAuthenticatedUserWrapper>
    )
  }
}
